import { format } from "date-fns";
import { Instrument } from "./instrument_type";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

interface Props {
  data: Instrument[];
  onSelect?: (device: Instrument) => void;
  selectedId?: number | null;
}

const PumpControllerTable = ({ data, onSelect, selectedId }: Props) => {
  const pumps = data.filter(
    (d) =>
      d.deviceType === "Pump Controller" ||
      d.deviceType === "Pump Comtroller",
  );

  if (pumps.length === 0) return null;

  return (
    <Table
     
      className="table table-hover table-responsive rounded border table-bordered align-middle text-center mb-0"
    >
      <TableHeader className="bg-light-primary">
        {/* Main Header Row */}
        <TableRow>
          <TableHead
            rowSpan={2}
            className="text-center align-middle border-end"
          >
            Instrument
          </TableHead>

          <TableHead
            colSpan={3}
            className="text-center border-start border-end"
          >
            Readings
          </TableHead>

          <TableHead
            colSpan={3}
            className="text-center border-start border-end"
          >
            Details
          </TableHead>

          <TableHead
            colSpan={2}
            className="text-center border-start border-end"
          >
            Status
          </TableHead>

          <TableHead
            rowSpan={2}
            className="text-center align-middle border-start"
          >
            Alert
          </TableHead>

          <TableHead
            rowSpan={2}
            className="text-center align-middle border-start"
          >
            Action
          </TableHead>
        </TableRow>

        {/* Sub Header Row */}
        <TableRow>
          <TableHead className="text-center border-start">
            Temp (°C)
          </TableHead>

          <TableHead className="text-center">
            CT (A)
          </TableHead>

          <TableHead className="text-center border-end">
            Mode
          </TableHead>

          <TableHead className="text-center">
            Local / Remote
          </TableHead>

          <TableHead className="text-center">
            FW
          </TableHead>

          <TableHead className="text-center border-end">
            Sys. Health
          </TableHead>

          <TableHead className="text-center">
            On / Off
          </TableHead>

          <TableHead className="text-center border-end">
            Faults
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {pumps.map((device) => {
          const hasAlert =
            !!device.highTemperatureAlarm ||
            !!device.p_CurrentFault ||
            !!device.p_PhaseFault ||
            !!device.p_OverCurrentBit ||
            !!device.p_UnderCurrentBit ||
            device.p_SysHealthy == 1 ||
            !!device.p_Phase1 ||
            !!device.p_Phase2 ||
            !!device.p_Phase3;

          const faults = [
            device.p_CurrentFault ? "Current" : null,
            device.p_PhaseFault ? "Phase" : null,
          ]
            .filter(Boolean)
            .join(", ");

          return (
            <TableRow
              key={device.id}
              className={`cursor-pointer ${
                selectedId === device.id ? "selected-card" : ""
              }`}
              onClick={() => onSelect?.(device)}
            >
              {/* Instrument */}
              <TableCell className="text-center text-nowrap border-end">
                <p className="fw-medium mb-0">
                  {device.instrumentName ?? "-"}
                </p>

                <p className="small mb-0">
                  {device.dateTime
                    ? format(
                        new Date(device.dateTime),
                        "yyyy-MM-dd HH:mm:ss",
                      )
                    : "-"}
                </p>
              </TableCell>

              {/* Temp */}
              <TableCell className="text-center border-start">
                {device.temperature ?? "-"}
              </TableCell>

              {/* CT */}
              <TableCell className="text-center">
                {device.ctValue ?? "-"}
              </TableCell>

              {/* Mode */}
              <TableCell className="text-center border-end">
                {device.auto_Manual == 0 ? "Auto" : "Manual"}
              </TableCell>

              {/* Local / Remote */}
              <TableCell className="text-center">
                {device.p_LocalRemote == 1 ? "Local" : "Remote"}
              </TableCell>

              {/* FW */}
              <TableCell className="text-center">
                {device.firmwareVersion ?? "-"}
              </TableCell>

              {/* System Health */}
              <TableCell className="text-center border-end">
                <span
                  className={
                    device.p_SysHealthy == 1
                      ? "text-danger fw-medium"
                      : "text-success fw-medium"
                  }
                >
                  {device.p_SysHealthy == 1 ? "Fault" : "Healthy"}
                </span>
              </TableCell>

              {/* ON/OFF */}
              <TableCell className="text-center">
                <span
                  className={
                    device.status == 1
                      ? "text-success fw-bold"
                      : "text-danger fw-bold"
                  }
                >
                  {device.status == 1 ? "ON" : "OFF"}
                </span>
              </TableCell>

              {/* Faults */}
              <TableCell className="text-center border-end">
                {faults ? (
                  <span className="text-danger fw-medium">
                    {faults}
                  </span>
                ) : (
                  <span className="text-success">—</span>
                )}
              </TableCell>

              {/* Alert */}
              <TableCell className="text-center border-start">
                <span
                  className={`device-alert-indicator ${
                    hasAlert ? "alarm" : "ok"
                  }`}
                  title={hasAlert ? "Alarm" : "OK"}
                  aria-label={hasAlert ? "Alarm" : "OK"}
                />
              </TableCell>

              {/* Action */}
              <TableCell className="text-center border-start">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect?.(device);
                  }}
                >
                  Details
                </button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default PumpControllerTable;